import { useQuery } from '@tanstack/react-query';

import type { QueryFunction } from '@frontend/api';
import { keysToCamelCaseDeep } from '@frontend/utils';

import type { DxfScene } from './types';

export type GetDxfSceneQueryKeyType = readonly ['dxfScene', string];

export const getDxfSceneQueryKey = (
  authenticatedUrl: string,
): GetDxfSceneQueryKeyType => ['dxfScene', authenticatedUrl] as const;

/**
 * Fetch the DXF viewer scene payload from a presigned scene-cache URL.
 */
export const useGetDxfScene: QueryFunction<
  DxfScene | null,
  [string | null | undefined],
  GetDxfSceneQueryKeyType
> = (authenticatedUrl, { queryOptions } = {}) => {
  const queryKey = getDxfSceneQueryKey(authenticatedUrl ?? '');

  return useQuery({
    ...queryOptions,
    queryKey,
    enabled: Boolean(authenticatedUrl) && (queryOptions?.enabled ?? true),
    queryFn: async ({ signal }) => {
      if (!authenticatedUrl) return null;

      const response = await fetch(authenticatedUrl, { method: 'GET', signal });

      if (!response.ok) {
        throw new Error('Failed to fetch DXF scene');
      }

      return keysToCamelCaseDeep(await response.json()) as DxfScene;
    },
  });
};
